import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { setCurrentUser } from '../../ducks/user.duck';
import { sendInquiry } from '../ListingPage/ListingPage.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { types as sdkTypes } from '../../util/sdkLoader';
import { TransactionProcessAlias } from '../../util/enums';
import { isUserAuthorized } from '../../util/userHelpers';
import {
  BOOKING_INQUIRY_STEPS,
  DECOR_SERVICE_OPTIONS,
  DIETARY_RESTRICTION_OPTIONS,
  FAVORITE_FOOD_OPTIONS,
  getNextInquiryStep,
  getPreviousInquiryStep,
  isBookingInquiryComplete,
  isInquiryStepComplete,
  KITCHEN_AVAILABILITY_OPTIONS,
  normalizeEventDate,
  normalizeEventTime,
  normalizeEventTimeSelection,
  normalizeInquiryPhoneNumber,
  normalizeKitchenAvailability,
  normalizeOccasionType,
  normalizeVenueAddress,
  normalizeVenueAndContact,
  OCCASION_OPTIONS,
  toggleDietaryRestriction,
  toggleDecorServiceSelection,
  toggleMultiSelectOption,
} from './bookingInquiryConfig';

const { UUID } = sdkTypes;

export const BOOKING_INQUIRY_DRAFTS_KEY = 'bookingInquiryDrafts';

const normalizeGuestCount = value => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.max(0, Math.trunc(numericValue)) : 0;
};

const normalizeGuestLimit = value => {
  const numericValue = Number(value);
  const integerValue = Math.trunc(numericValue);
  return Number.isFinite(numericValue) && integerValue > 0 ? integerValue : null;
};

const optionLabel = (options, value) =>
  options.find(option => option.id === value)?.label || value || '';

const optionLabels = (options, values = []) =>
  (values || [])
    .map(value => optionLabel(options, value))
    .filter(Boolean)
    .join(', ');

export const createBookingInquiryMessage = answers => {
  const inquiry = answers || {};
  const allergiesAndDiet = inquiry.allergiesAndDiet || {};
  const favoriteFoods = inquiry.favoriteFoods || {};
  const decorAndSetup = inquiry.decorAndSetup || {};
  const partySize = inquiry.partySize || {};
  const eventTiming = inquiry.eventTiming || {};
  const venueAndContact = inquiry.venueAndContact || {};
  const venueAddress =
    venueAndContact.venueAddress?.selectedPlace?.address ||
    venueAndContact.venueAddress?.search ||
    '';
  const lines = [
    `Booking inquiry: ${optionLabel(OCCASION_OPTIONS, inquiry.occasionType)}`,
    eventTiming.date ? `Event date: ${eventTiming.date}` : null,
    eventTiming.appetizerArrivalTime
      ? `Appetizer arrival: ${eventTiming.appetizerArrivalTime}`
      : null,
    eventTiming.mainServingTime ? `Main serving time: ${eventTiming.mainServingTime}` : null,
    `Party size: ${partySize.adults || 0} adults, ${partySize.children || 0} children`,
    `Allergies & diet: ${optionLabels(
      DIETARY_RESTRICTION_OPTIONS,
      allergiesAndDiet.selectedOptions
    ) || 'None shared'}`,
    allergiesAndDiet.additionalDetails
      ? `Dietary details: ${allergiesAndDiet.additionalDetails}`
      : null,
    `Favorite foods: ${optionLabels(FAVORITE_FOOD_OPTIONS, favoriteFoods.selectedOptions) ||
      'None shared'}`,
    favoriteFoods.additionalDetails
      ? `Favorite-food details: ${favoriteFoods.additionalDetails}`
      : null,
    inquiry.foodsToAvoid ? `Foods to avoid: ${inquiry.foodsToAvoid}` : null,
    `Decor & setup: ${optionLabels(DECOR_SERVICE_OPTIONS, decorAndSetup.selectedServices) ||
      'None shared'}`,
    decorAndSetup.colorScheme ? `Color scheme: ${decorAndSetup.colorScheme}` : null,
    `Kitchen: ${optionLabel(KITCHEN_AVAILABILITY_OPTIONS, inquiry.kitchenAvailability)}`,
    venueAddress ? `Venue: ${venueAddress}` : null,
    venueAndContact.fullName ? `Contact: ${venueAndContact.fullName}` : null,
    venueAndContact.phoneNumber ? `Phone: ${venueAndContact.phoneNumber}` : null,
  ];

  return lines.filter(Boolean).join('\n');
};

const createDraft = ({
  listingId,
  chefId,
  customerId,
  origin,
  selectedServiceId = null,
  currentStep = 1,
  venueAndContactPrefill = {},
}) => ({
  listingId,
  chefId,
  customerId,
  origin,
  selectedServiceId,
  currentStep,
  completedSteps: Array.from({ length: Math.max(currentStep - 1, 0) }, (_, index) => index + 1),
  answers: {
    allergiesAndDiet: {
      selectedOptions: [],
      additionalDetails: '',
    },
    favoriteFoods: {
      selectedOptions: [],
      additionalDetails: '',
    },
    foodsToAvoid: '',
    decorAndSetup: {
      selectedServices: [],
      colorScheme: '',
    },
    partySize: {
      adults: 0,
      children: 0,
    },
    eventTiming: {
      date: null,
      appetizerArrivalTime: null,
      mainServingTime: null,
    },
    kitchenAvailability: null,
    venueAndContact: normalizeVenueAndContact(null, venueAndContactPrefill),
    occasionType: null,
  },
  navigation: {
    requestedStep: null,
    skipped: false,
    direction: null,
  },
  submission: {
    inProgress: false,
    error: null,
    transactionId: null,
  },
  draftSave: {
    inProgress: false,
    error: null,
    lastSavedAt: null,
  },
});

const normalizeSavedMultiSelect = answer => ({
  selectedOptions: Array.isArray(answer?.selectedOptions) ? answer.selectedOptions : [],
  additionalDetails: typeof answer?.additionalDetails === 'string' ? answer.additionalDetails : '',
});

const hydrateDraft = (draftContext, persistedDraft) => {
  const draft = createDraft(draftContext);
  const savedAnswers = persistedDraft?.answers;
  const isMatchingDraft =
    savedAnswers &&
    persistedDraft.listingId === draftContext.listingId &&
    persistedDraft.chefId === draftContext.chefId &&
    persistedDraft.customerId === draftContext.customerId;

  if (!isMatchingDraft) {
    return draft;
  }

  const savedDecor = savedAnswers.decorAndSetup || {};
  const savedPartySize = savedAnswers.partySize || {};
  const savedEventTiming = savedAnswers.eventTiming || {};

  return {
    ...draft,
    origin: persistedDraft.origin || draft.origin,
    selectedServiceId: persistedDraft.selectedServiceId || draft.selectedServiceId,
    completedSteps: Array.from(
      new Set([...(draft.completedSteps || []), ...(persistedDraft.completedSteps || [])])
    ),
    answers: {
      allergiesAndDiet: normalizeSavedMultiSelect(savedAnswers.allergiesAndDiet),
      favoriteFoods: normalizeSavedMultiSelect(savedAnswers.favoriteFoods),
      foodsToAvoid: typeof savedAnswers.foodsToAvoid === 'string' ? savedAnswers.foodsToAvoid : '',
      decorAndSetup: {
        selectedServices: Array.isArray(savedDecor.selectedServices)
          ? savedDecor.selectedServices
          : [],
        colorScheme: typeof savedDecor.colorScheme === 'string' ? savedDecor.colorScheme : '',
      },
      partySize: {
        adults: normalizeGuestCount(savedPartySize.adults),
        children: normalizeGuestCount(savedPartySize.children),
      },
      eventTiming: {
        date: normalizeEventDate(savedEventTiming.date),
        appetizerArrivalTime: normalizeEventTime(savedEventTiming.appetizerArrivalTime),
        mainServingTime: normalizeEventTime(savedEventTiming.mainServingTime),
      },
      kitchenAvailability: normalizeKitchenAvailability(savedAnswers.kitchenAvailability),
      venueAndContact: normalizeVenueAndContact(
        savedAnswers.venueAndContact,
        draftContext.venueAndContactPrefill
      ),
      occasionType: normalizeOccasionType(savedAnswers.occasionType),
    },
    draftSave: {
      inProgress: false,
      error: null,
      lastSavedAt: persistedDraft.savedAt || null,
    },
  };
};

export const createPersistedBookingInquiryDraft = (draft, savedAt) => ({
  schemaVersion: 1,
  listingId: draft.listingId,
  chefId: draft.chefId,
  customerId: draft.customerId,
  origin: draft.origin,
  selectedServiceId: draft.selectedServiceId,
  currentStep: draft.currentStep,
  completedSteps: draft.completedSteps,
  answers: draft.answers,
  savedAt,
});

const showListingPayloadCreator = (
  { listingId, config },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage'],
    'fields.image': [
      'variants.square-xsmall',
      'variants.square-xsmall2x',
      'variants.square-small',
      'variants.square-small2x',
    ],
  };

  return sdk.listings
    .show(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(response, { listingFields }));
      return response;
    })
    .catch(error => rejectWithValue(storableError(error)));
};

export const showListingThunk = createAsyncThunk(
  'BookingInquiryPage/showListing',
  showListingPayloadCreator
);

const submitBookingInquiryPayloadCreator = async (
  { listingId, listing },
  { dispatch, getState, rejectWithValue }
) => {
  const draft = getState().BookingInquiryPage.drafts[listingId];

  if (!listing?.id || !draft || !isBookingInquiryComplete(draft.answers)) {
    return rejectWithValue(
      storableError(new Error('Complete all required inquiry details before submitting'))
    );
  }

  const message = createBookingInquiryMessage(draft.answers);
  const protectedData = {
    inquiryMessage: message,
    bookingInquiry: {
      ...draft.answers,
      ...(draft.selectedServiceId ? { selectedServiceId: draft.selectedServiceId } : {}),
    },
  };

  try {
    const transactionId = await dispatch(
      sendInquiry(listing, message, {
        protectedData,
        processAlias: TransactionProcessAlias.DEFAULT_INQUIRY,
        // Once the transaction exists, retrying because its optional first message failed
        // could create a duplicate inquiry. The submitted details remain in protected data.
        initialMessageFailureIsFatal: false,
      })
    );

    return { listingId, transactionId: transactionId.uuid };
  } catch (error) {
    return rejectWithValue(storableError(error));
  }
};

export const submitBookingInquiryThunk = createAsyncThunk(
  'BookingInquiryPage/submitBookingInquiry',
  submitBookingInquiryPayloadCreator
);

const saveBookingInquiryDraftPayloadCreator = async (
  { listingId },
  { dispatch, getState, extra: sdk, rejectWithValue }
) => {
  const state = getState();
  const draft = state.BookingInquiryPage.drafts[listingId];
  const currentUser = state.user?.currentUser;

  if (!draft || !currentUser?.id) {
    return rejectWithValue(storableError(new Error('Unable to save this inquiry draft')));
  }

  const profilePrivateData = currentUser.attributes?.profile?.privateData || {};
  const existingDrafts = profilePrivateData[BOOKING_INQUIRY_DRAFTS_KEY];
  const savedAt = new Date().toISOString();
  const bookingInquiryDrafts = {
    ...(existingDrafts && typeof existingDrafts === 'object' ? existingDrafts : {}),
    [listingId]: createPersistedBookingInquiryDraft(draft, savedAt),
  };

  try {
    const response = await sdk.currentUser.updateProfile(
      {
        privateData: {
          ...profilePrivateData,
          [BOOKING_INQUIRY_DRAFTS_KEY]: bookingInquiryDrafts,
        },
      },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': [
          'variants.square-xsmall',
          'variants.square-xsmall2x',
          'variants.square-small',
          'variants.square-small2x',
        ],
      }
    );
    const entities = denormalisedResponseEntities(response);

    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }

    dispatch(setCurrentUser(entities[0]));
    return { listingId, savedAt };
  } catch (error) {
    return rejectWithValue(storableError(error));
  }
};

export const saveBookingInquiryDraftThunk = createAsyncThunk(
  'BookingInquiryPage/saveBookingInquiryDraft',
  saveBookingInquiryDraftPayloadCreator
);

const initialState = {
  listingId: null,
  showListingInProgress: false,
  showListingError: null,
  drafts: {},
};

const bookingInquiryPageSlice = createSlice({
  name: 'BookingInquiryPage',
  initialState,
  reducers: {
    initializeDraft: (state, action) => {
      const {
        listingId,
        chefId,
        customerId,
        origin,
        selectedServiceId,
        currentStep = 1,
        venueAndContactPrefill = {},
        persistedDraft = null,
      } = action.payload;
      const existingDraft = state.drafts[listingId];
      const contextChanged =
        existingDraft &&
        (existingDraft.chefId !== chefId || existingDraft.customerId !== customerId);

      if (!existingDraft || contextChanged) {
        state.drafts[listingId] = hydrateDraft(
          {
            listingId,
            chefId,
            customerId,
            origin,
            selectedServiceId,
            currentStep,
            venueAndContactPrefill,
          },
          persistedDraft
        );
      } else {
        existingDraft.currentStep = currentStep;
        existingDraft.completedSteps = Array.from(
          new Set([
            ...(existingDraft.completedSteps || []),
            ...Array.from({ length: Math.max(currentStep - 1, 0) }, (_, index) => index + 1),
          ])
        );
        existingDraft.answers.favoriteFoods = existingDraft.answers.favoriteFoods || {
          selectedOptions: [],
          additionalDetails: '',
        };
        existingDraft.answers.foodsToAvoid = existingDraft.answers.foodsToAvoid || '';
        existingDraft.answers.decorAndSetup = existingDraft.answers.decorAndSetup || {
          selectedServices: [],
          colorScheme: '',
        };
        existingDraft.answers.partySize = existingDraft.answers.partySize || {
          adults: 0,
          children: 0,
        };
        existingDraft.answers.eventTiming = existingDraft.answers.eventTiming || {
          date: null,
          appetizerArrivalTime: null,
          mainServingTime: null,
        };
        existingDraft.answers.eventTiming.date = normalizeEventDate(
          existingDraft.answers.eventTiming.date
        );
        existingDraft.answers.eventTiming.appetizerArrivalTime = normalizeEventTime(
          existingDraft.answers.eventTiming.appetizerArrivalTime
        );
        existingDraft.answers.eventTiming.mainServingTime = normalizeEventTime(
          existingDraft.answers.eventTiming.mainServingTime
        );
        existingDraft.answers.kitchenAvailability = normalizeKitchenAvailability(
          existingDraft.answers.kitchenAvailability
        );
        existingDraft.answers.venueAndContact = normalizeVenueAndContact(
          existingDraft.answers.venueAndContact,
          venueAndContactPrefill
        );
        existingDraft.answers.occasionType = normalizeOccasionType(
          existingDraft.answers.occasionType
        );
        existingDraft.submission = existingDraft.submission || {
          inProgress: false,
          error: null,
          transactionId: null,
        };
        existingDraft.draftSave = existingDraft.draftSave || {
          inProgress: false,
          error: null,
          lastSavedAt: null,
        };
        existingDraft.answers.partySize.adults = normalizeGuestCount(
          existingDraft.answers.partySize.adults
        );
        existingDraft.answers.partySize.children = normalizeGuestCount(
          existingDraft.answers.partySize.children
        );
        if (origin) {
          existingDraft.origin = origin;
        }
        if (selectedServiceId) {
          existingDraft.selectedServiceId = selectedServiceId;
        }
      }
    },
    toggleDietaryOption: (state, action) => {
      const { listingId, optionId } = action.payload;
      const draft = state.drafts[listingId];
      if (draft) {
        const answer = draft.answers.allergiesAndDiet;
        answer.selectedOptions = toggleDietaryRestriction(answer.selectedOptions, optionId);
      }
    },
    updateDietaryDetails: (state, action) => {
      const { listingId, additionalDetails } = action.payload;
      const draft = state.drafts[listingId];
      if (draft) {
        draft.answers.allergiesAndDiet.additionalDetails = additionalDetails;
      }
    },
    toggleFavoriteFoodOption: (state, action) => {
      const { listingId, optionId } = action.payload;
      const draft = state.drafts[listingId];
      if (draft) {
        const answer = draft.answers.favoriteFoods;
        answer.selectedOptions = toggleMultiSelectOption(answer.selectedOptions, optionId);
      }
    },
    updateFavoriteFoodDetails: (state, action) => {
      const { listingId, additionalDetails } = action.payload;
      const draft = state.drafts[listingId];
      if (draft) {
        draft.answers.favoriteFoods.additionalDetails = additionalDetails;
      }
    },
    updateFoodsToAvoid: (state, action) => {
      const { listingId, details } = action.payload;
      const draft = state.drafts[listingId];
      if (draft) {
        draft.answers.foodsToAvoid = details;
      }
    },
    toggleDecorService: (state, action) => {
      const { listingId, serviceId } = action.payload;
      const draft = state.drafts[listingId];
      if (draft) {
        const answer = draft.answers.decorAndSetup;
        answer.selectedServices = toggleDecorServiceSelection(answer.selectedServices, serviceId);
      }
    },
    updateDecorColorScheme: (state, action) => {
      const { listingId, colorScheme } = action.payload;
      const draft = state.drafts[listingId];
      if (draft) {
        draft.answers.decorAndSetup.colorScheme = colorScheme;
      }
    },
    adjustPartySizeCount: (state, action) => {
      const { listingId, guestType, direction, maxGuests } = action.payload;
      const draft = state.drafts[listingId];
      const isKnownGuestType = guestType === 'adults' || guestType === 'children';
      const adjustment = direction === 'increment' ? 1 : direction === 'decrement' ? -1 : 0;

      if (draft && isKnownGuestType && adjustment) {
        const answer = draft.answers.partySize || { adults: 0, children: 0 };
        const adults = normalizeGuestCount(answer.adults);
        const children = normalizeGuestCount(answer.children);
        const maximumGuests = normalizeGuestLimit(maxGuests);
        const totalGuests = adults + children;

        draft.answers.partySize = { adults, children };
        if (adjustment > 0 && maximumGuests !== null && totalGuests >= maximumGuests) {
          return;
        }

        const currentCount = draft.answers.partySize[guestType];
        draft.answers.partySize[guestType] = Math.max(0, currentCount + adjustment);
      }
    },
    updateEventTiming: (state, action) => {
      const { listingId, field, value } = action.payload;
      const draft = state.drafts[listingId];
      const isDateField = field === 'date';
      const isTimeField = field === 'appetizerArrivalTime' || field === 'mainServingTime';

      if (draft && (isDateField || isTimeField)) {
        const answer = draft.answers.eventTiming || {
          date: null,
          appetizerArrivalTime: null,
          mainServingTime: null,
        };
        draft.answers.eventTiming = answer;
        answer[field] = isDateField
          ? normalizeEventDate(value)
          : normalizeEventTimeSelection(value);
      }
    },
    updateKitchenAvailability: (state, action) => {
      const { listingId, availability } = action.payload;
      const draft = state.drafts[listingId];

      if (draft) {
        draft.answers.kitchenAvailability = normalizeKitchenAvailability(availability);
      }
    },
    updateVenueAndContact: (state, action) => {
      const { listingId, field, value } = action.payload;
      const draft = state.drafts[listingId];

      if (draft) {
        const answer = draft.answers.venueAndContact || normalizeVenueAndContact();
        draft.answers.venueAndContact = answer;

        if (field === 'venueAddress') {
          answer.venueAddress = normalizeVenueAddress(value);
        } else if (field === 'fullName') {
          answer.fullName = typeof value === 'string' ? value : '';
        } else if (field === 'phoneNumber') {
          answer.phoneNumber = normalizeInquiryPhoneNumber(value);
        }
      }
    },
    updateOccasionType: (state, action) => {
      const { listingId, occasionType } = action.payload;
      const draft = state.drafts[listingId];

      if (draft) {
        draft.answers.occasionType = normalizeOccasionType(occasionType);
      }
    },
    requestNextStep: (state, action) => {
      const { listingId, skipped, currentStepId, nextStepId, canNavigate } = action.payload;
      const draft = state.drafts[listingId];
      if (draft) {
        draft.completedSteps = Array.from(
          new Set([...(draft.completedSteps || []), currentStepId])
        );
        draft.navigation = { requestedStep: nextStepId, skipped: !!skipped, direction: 'forward' };
        if (canNavigate) {
          draft.currentStep = nextStepId;
        }
      }
    },
    requestPreviousStep: (state, action) => {
      const { listingId, previousStepId } = action.payload;
      const draft = state.drafts[listingId];
      if (draft) {
        draft.currentStep = previousStepId;
        draft.navigation = {
          requestedStep: previousStepId,
          skipped: false,
          direction: 'back',
        };
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(showListingThunk.pending, (state, action) => {
        state.listingId = action.meta.arg.listingId;
        state.showListingInProgress = true;
        state.showListingError = null;
      })
      .addCase(showListingThunk.fulfilled, state => {
        state.showListingInProgress = false;
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        state.showListingInProgress = false;
        state.showListingError = action.payload;
      })
      .addCase(submitBookingInquiryThunk.pending, (state, action) => {
        const draft = state.drafts[action.meta.arg.listingId];
        if (draft) {
          draft.submission = draft.submission || {
            inProgress: false,
            error: null,
            transactionId: null,
          };
          draft.submission.inProgress = true;
          draft.submission.error = null;
        }
      })
      .addCase(submitBookingInquiryThunk.fulfilled, (state, action) => {
        const draft = state.drafts[action.payload.listingId];
        if (draft) {
          draft.submission = draft.submission || {};
          draft.submission.inProgress = false;
          draft.submission.error = null;
          draft.submission.transactionId = action.payload.transactionId;
        }
      })
      .addCase(submitBookingInquiryThunk.rejected, (state, action) => {
        const draft = state.drafts[action.meta.arg.listingId];
        if (draft) {
          draft.submission = draft.submission || {
            inProgress: false,
            error: null,
            transactionId: null,
          };
          draft.submission.inProgress = false;
          draft.submission.error = action.payload || action.error;
        }
      })
      .addCase(saveBookingInquiryDraftThunk.pending, (state, action) => {
        const draft = state.drafts[action.meta.arg.listingId];
        if (draft) {
          draft.draftSave = draft.draftSave || {};
          draft.draftSave.inProgress = true;
          draft.draftSave.error = null;
        }
      })
      .addCase(saveBookingInquiryDraftThunk.fulfilled, (state, action) => {
        const draft = state.drafts[action.payload.listingId];
        if (draft) {
          draft.draftSave = {
            inProgress: false,
            error: null,
            lastSavedAt: action.payload.savedAt,
          };
        }
      })
      .addCase(saveBookingInquiryDraftThunk.rejected, (state, action) => {
        const draft = state.drafts[action.meta.arg.listingId];
        if (draft) {
          draft.draftSave = draft.draftSave || {};
          draft.draftSave.inProgress = false;
          draft.draftSave.error = action.payload || action.error;
        }
      });
  },
});

export const {
  adjustPartySizeCount,
  initializeDraft,
  requestPreviousStep,
  requestNextStep,
  toggleDecorService,
  toggleFavoriteFoodOption,
  toggleDietaryOption,
  updateDecorColorScheme,
  updateFavoriteFoodDetails,
  updateFoodsToAvoid,
  updateDietaryDetails,
  updateEventTiming,
  updateKitchenAvailability,
  updateOccasionType,
  updateVenueAndContact,
} = bookingInquiryPageSlice.actions;

export const goToNextStep = ({ listingId, skipped = false, currentStep }) => (
  dispatch,
  getState
) => {
  const draft = getState().BookingInquiryPage.drafts[listingId];
  const currentStepId = currentStep || draft?.currentStep || 1;
  const currentStepConfig = BOOKING_INQUIRY_STEPS.find(step => step.id === currentStepId);
  const nextStep = getNextInquiryStep(currentStepId);
  const canSkip = currentStepConfig?.isOptional !== false;
  const isComplete = isInquiryStepComplete(currentStepId, draft?.answers);
  const canAdvance = (!skipped || canSkip) && isComplete;
  const canNavigate = canAdvance && !!nextStep?.routeName;

  if (canAdvance) {
    dispatch(
      requestNextStep({
        listingId,
        skipped,
        currentStepId,
        nextStepId: nextStep?.id || null,
        canNavigate,
      })
    );
  }

  return {
    nextStep,
    canNavigate,
    isComplete,
  };
};

export const goToPreviousStep = ({ listingId, currentStep }) => (dispatch, getState) => {
  const draft = getState().BookingInquiryPage.drafts[listingId];
  const previousStep = getPreviousInquiryStep(currentStep || draft?.currentStep || 1);

  if (previousStep) {
    dispatch(requestPreviousStep({ listingId, previousStepId: previousStep.id }));
  }

  return {
    previousStep,
    canNavigate: !!previousStep?.routeName,
  };
};

export const submitBookingInquiry = params => (dispatch, getState) => {
  const submission = getState().BookingInquiryPage.drafts[params.listingId]?.submission;

  if (submission?.transactionId) {
    return Promise.resolve(new UUID(submission.transactionId));
  }

  if (submission?.inProgress) {
    return Promise.reject(new Error('Inquiry submission is already in progress'));
  }

  return dispatch(submitBookingInquiryThunk(params))
    .unwrap()
    .then(result => new UUID(result.transactionId));
};

export const saveBookingInquiryDraft = params => (dispatch, getState) => {
  const draftSave = getState().BookingInquiryPage.drafts[params.listingId]?.draftSave;

  if (draftSave?.inProgress) {
    return Promise.reject(new Error('Inquiry draft save is already in progress'));
  }

  return dispatch(saveBookingInquiryDraftThunk(params)).unwrap();
};

export default bookingInquiryPageSlice.reducer;

export const loadData = (params, search, config) => (dispatch, getState) => {
  const listingId = new UUID(params.id);
  const currentUser = getState().user?.currentUser;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || isUserAuthorized(currentUser);

  if (!canFetchData) {
    return Promise.resolve();
  }

  return dispatch(showListingThunk({ listingId, config }));
};
